import { Msg } from "rujira.js";

// A function is not transaction data, but it can close over some: a message
// that prepares its execution lazily keeps the amount inside a callback. Its
// source text is the same for every amount, so key functions by identity
// instead: the same callback keeps its key, a new closure never matches an
// earlier one.
const functionIds = new WeakMap<object, number>();
let nextFunctionId = 0;
const functionKey = (value: object): string => {
  let id = functionIds.get(value);
  if (id === undefined) {
    id = ++nextFunctionId;
    functionIds.set(value, id);
  }
  return `function#${id}`;
};

const stableValue = (
  value: unknown,
  seen: WeakSet<object>,
  omitQueryClient = false
): string => {
  if (typeof value === "bigint") return `bigint:${value}`;
  if (typeof value === "function") return functionKey(value);
  if (value === null || typeof value !== "object")
    return `${typeof value}:${String(value)}`;
  if (value instanceof Uint8Array)
    return `bytes:${Array.from(value).join(",")}`;
  if (seen.has(value)) return "[circular]";

  seen.add(value);
  const entries = Object.keys(value)
    // Query clients are injected after construction and are not transaction data.
    .filter((key) => !omitQueryClient || key !== "q")
    .sort()
    .map(
      (key) =>
        `${key}:${stableValue((value as Record<string, unknown>)[key], seen)}`
    );
  seen.delete(value);

  const type = value.constructor?.name || "Object";
  return `${type}{${entries.join("|")}}`;
};

/** A deterministic key for the transaction data represented by a Msg. */
export const transactionMessageKey = (msg: Msg | null): string | null =>
  msg ? stableValue(msg, new WeakSet(), true) : null;
